import { inject, Injectable } from '@angular/core';
import { HubConnectionState } from '@microsoft/signalr';
import { Selector, Store } from '@ngrx/store';
import { environment } from '@rocksoft/environment';
import { firstValueFrom, Subscription } from 'rxjs';
import { distinctUntilChanged, take } from 'rxjs/operators';
import { MessageLogger } from '../sub-features/logging/interfaces/message-logger.interface';
import { ToastService } from '../sub-features/toast/toast.service';
import {
  AppManagementHubService,
  AppleSyncHubService,
  DeviceTaskManagementHubService,
  HubService,
  InRoomHubService,
  LocationsHubService,
  MdmHubService,
} from './hubs';
import { HubType, RouteGroupRule, SignalREventHandler } from './route-group-rule';
import { SIGNALR_ROUTE_RULES } from './signalr-route-rules.token';
import { SIGNALR_URL_SELECTOR } from './signalr-url-selector.token';

interface GroupData {
  rule: RouteGroupRule;
  eventHandlers: SignalREventHandler[];
  hub: HubType;
}

const INITIAL_CONNECTION_RETRY_DELAYS_MS = [0, 1000, 2000, 5000];
const MANUAL_RECONNECT_MAX_DELAY_MS = 30000;
const MANUAL_RECONNECT_GIVE_UP_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Automatically manages SignalR group memberships based on route changes.
 *
 * This service:
 * - Listens to router URL changes
 * - Evaluates route group rules to determine which SignalR groups are needed
 * - Automatically joins new groups and leaves old groups
 * - Sets up and tears down event handlers automatically
 *
 * Usage:
 * - Initialize once in AppComponent ngOnInit
 * - Cleanup in AppComponent ngOnDestroy
 * - Add new rules in route-group-rules.config.ts
 */
@Injectable({ providedIn: 'root' })
export class RouteSignalRGroupService {
  private readonly store = inject(Store);
  private readonly ToastService = inject(ToastService);
  private readonly messageLogger = inject(MessageLogger);
  private readonly appManagementHub = inject(AppManagementHubService);
  private readonly locationsHub = inject(LocationsHubService);
  private readonly deviceTaskManagementHub = inject(DeviceTaskManagementHubService);
  private readonly mdmHub = inject(MdmHubService);
  private readonly inroomHub = inject(InRoomHubService);
  private readonly appleSyncHub = inject(AppleSyncHubService);

  private currentGroups = new Map<string, GroupData>();
  private reconnectingHubs = new Set<HubType>();
  private isCleaningUp = false;
  private subscription: Subscription | null = null;
  private hubsReady = false;
  private latestUrl = '';
  private rules: RouteGroupRule[];
  private readonly hubServices: Map<HubType, HubService>;
  private readonly urlSelector: Selector<object, string>;

  // The constructor receives two dependency-injection tokens:
  // - `SIGNALR_URL_SELECTOR`: supplies the concrete router URL selector
  //   (e.g. `RouterStoreSelectors.selectUrl`) from the app, avoiding a
  //   direct import of `@era/stores` from this shared service.
  // - `SIGNALR_ROUTE_RULES`: a multi-provider used by feature modules to
  //   register their own route-to-SignalR-group rules. This lets features
  //   contribute rules without the shared SignalR module importing those
  //   feature modules directly, preventing tight coupling and circular
  //   dependency evaluation.
  constructor() {
    const urlSelector = inject<Selector<object, string>>(SIGNALR_URL_SELECTOR);
    const ruleGroups = inject(SIGNALR_ROUTE_RULES, { optional: true });

    this.urlSelector = urlSelector;
    const allRules = ruleGroups ? ruleGroups.flat() : [];

    // Filter out virtual-room rules if feature flag is disabled
    const isVirtualRoomEnabled = environment.features.virtual_room.enabled;
    this.rules = isVirtualRoomEnabled ? allRules : allRules.filter((rule) => rule.hub !== 'virtual-room');

    // Register all hubs - add new hubs here when needed
    const hubServicesArray: [HubType, HubService][] = [
      ['app-management', this.appManagementHub],
      ['locations', this.locationsHub],
      ['device-task-management', this.deviceTaskManagementHub],
      ['mdm', this.mdmHub],
      ['apple-sync', this.appleSyncHub],
    ];

    // Only register inroomHub if virtual_room feature flag is enabled
    if (isVirtualRoomEnabled) {
      hubServicesArray.push(['virtual-room', this.inroomHub]);
    }

    this.hubServices = new Map<HubType, HubService>(hubServicesArray);
    this.registerHubLifecycleHandlers();
  }

  /**
   * Get the appropriate hub service based on hub type
   */
  private getHubService(hub: HubType): HubService {
    const hubService = this.hubServices.get(hub);
    if (!hubService) {
      throw new Error(`Unknown hub type: ${hub}`);
    }
    return hubService;
  }

  /**
   * Check if a hub is connected and ready to use
   */
  private isHubConnected(hubService: HubService): boolean {
    return hubService.hubConnection.state === HubConnectionState.Connected;
  }

  /**
   * Initialize the service - start listening to route changes
   */
  async initialize(): Promise<void> {
    if (this.subscription) {
      console.warn('RouteSignalRGroupService already initialized');
      return;
    }

    this.isCleaningUp = false;

    // Subscribe before connecting so a route emitted during startup is retained.
    // Replay the current URL after the hubs are ready for direct page loads.
    this.subscription = this.store
      .select(this.urlSelector)
      .pipe(distinctUntilChanged())
      .subscribe((url) => {
        this.latestUrl = url;
        if (this.hubsReady) {
          void this.handleRouteChange(url);
        }
      });

    await Promise.all(
      Array.from(this.hubServices.entries()).map(([hubType, hub]) => this.connectHubWithRetry(hubType, hub))
    );

    this.hubsReady = true;
    const currentUrl = await firstValueFrom(this.store.select(this.urlSelector).pipe(take(1)));
    if (currentUrl) {
      this.latestUrl = currentUrl;
    }
    await this.handleRouteChange(this.latestUrl);
  }

  /**
   * Handle route change - determine groups and update memberships
   */
  private async handleRouteChange(url: string): Promise<void> {
    try {
      if (!url) return;

      const newGroupsMap = await this.determineGroupsForRoute(url);
      await this.updateGroups(newGroupsMap);
    } catch (error: unknown) {
      this.ToastService.showError(`[SignalR] Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Update group memberships - leave old groups and join new ones
   */
  private async updateGroups(newGroupsMap: Map<string, RouteGroupRule>): Promise<void> {
    const newGroupKeys = new Set(newGroupsMap.keys());
    const currentGroupKeys = new Set(this.currentGroups.keys());

    // Leave old groups
    for (const groupKey of currentGroupKeys) {
      if (!newGroupKeys.has(groupKey)) {
        await this.leaveGroup(groupKey);
      }
    }

    // Join new groups
    for (const groupKey of newGroupKeys) {
      if (!currentGroupKeys.has(groupKey)) {
        const rule = newGroupsMap.get(groupKey);
        if (rule) {
          await this.joinGroup(groupKey, rule);
        }
      }
    }
  }

  /**
   * Leave a specific group and clean up its event handlers
   */
  private async leaveGroup(groupKey: string): Promise<void> {
    const groupData = this.currentGroups.get(groupKey);
    try {
      if (!groupData) return;

      const hubService = this.getHubService(groupData.hub);

      // Skip if hub is not connected
      if (!this.isHubConnected(hubService)) {
        this.currentGroups.delete(groupKey);
        return;
      }

      // Remove event handlers
      this.unregisterEventHandlers(hubService, groupData.eventHandlers);

      await hubService.hubConnection.invoke('LeaveGroup', groupKey);
      this.currentGroups.delete(groupKey);
    } catch (error) {
      console.warn(
        `[SignalR] Error leaving group ${groupKey}: ${error instanceof Error ? error.message : String(error)}`
      );

      // Log to Sentry/ApplicationInsights
      const errorMessage = error instanceof Error ? error.message : String(error);
      const logMessage = `[SignalR] Error leaving group ${groupKey}${groupData ? ` (hub: ${groupData.hub})` : ''}: ${errorMessage}`;
      this.messageLogger.logMessage(logMessage);

      // Still remove from currentGroups even if leave failed
      this.currentGroups.delete(groupKey);
    }
  }

  /**
   * Join a specific group and set up its event handlers
   */
  private async joinGroup(groupKey: string, rule: RouteGroupRule): Promise<void> {
    try {
      const hubService = this.getHubService(rule.hub);

      // Skip if hub is not connected
      if (!this.isHubConnected(hubService)) {
        return;
      }

      const eventHandlers = rule.eventHandlers ?? [];
      this.registerEventHandlers(hubService, eventHandlers);

      await hubService.hubConnection.invoke('JoinGroup', groupKey);

      this.currentGroups.set(groupKey, { rule, eventHandlers, hub: rule.hub });
    } catch (error) {
      if (!(error instanceof Error && error.message.includes('connection'))) {
        this.ToastService.showError(
          `[SignalR] Error joining group ${groupKey}: ${error instanceof Error ? error.message : String(error)}`
        );
      }
    }
  }

  /**
   * Determine which groups are needed for the current route
   */
  private async determineGroupsForRoute(url: string): Promise<Map<string, RouteGroupRule>> {
    const groups = new Map<string, RouteGroupRule>();

    for (const rule of this.rules) {
      const groupName = await this.evaluateRule(url, rule);
      if (groupName) {
        groups.set(groupName, rule);
      }
    }

    return groups;
  }

  /**
   * Evaluate a single rule against a URL
   */
  private async evaluateRule(url: string, rule: RouteGroupRule): Promise<string | null> {
    try {
      const match = url.match(rule.urlPattern);
      if (!match) return null;

      let groupName: string | null;

      // Check if groupName is a string or function
      if (typeof rule.groupName === 'string') {
        // Static group name
        groupName = rule.groupName;
      } else {
        // Dynamic group name - call the builder function
        groupName = await rule.groupName(this.store, match);
      }

      return groupName;
    } catch (error) {
      this.ToastService.showError(
        `[SignalR] Error evaluating group rule ${rule.name}: ${error instanceof Error ? error.message : String(error)}`
      );
      return null;
    }
  }

  /**
   * Cleanup - leave all groups and stop listening
   */
  async cleanup(): Promise<void> {
    this.isCleaningUp = true;
    this.hubsReady = false;
    this.latestUrl = '';

    for (const group of this.currentGroups.keys()) {
      await this.leaveGroup(group);
    }
    this.subscription?.unsubscribe();
    this.subscription = null;
  }

  private registerHubLifecycleHandlers(): void {
    Array.from(this.hubServices.entries()).forEach(([hubType, hubService]) => {
      hubService.hubConnection.onreconnected(() => {
        void this.restoreHubState(hubType);
      });

      hubService.hubConnection.onclose((error) => {
        if (this.isCleaningUp) {
          return;
        }

        void this.runManualReconnectLoop(hubType, hubService, error);
      });
    });
  }

  private async connectHubWithRetry(hubType: HubType, hubService: HubService): Promise<void> {
    let lastError: unknown;

    for (const delayMs of INITIAL_CONNECTION_RETRY_DELAYS_MS) {
      if (this.isCleaningUp) {
        return;
      }

      if (delayMs > 0) {
        await this.delay(delayMs);
      }

      try {
        await hubService.ensureConnected();
        return;
      } catch (error) {
        lastError = error;
      }
    }

    const errorMessage = lastError instanceof Error ? lastError.message : 'Unknown error';
    console.warn(`[SignalR] Failed to connect to ${hubType} hub: ${errorMessage}. Continuing without this hub.`);
    this.messageLogger.logMessage(
      `[SignalR] Failed to connect to ${hubType} hub: ${errorMessage}. Continuing without this hub.`
    );
  }

  private async runManualReconnectLoop(hubType: HubType, hubService: HubService, error?: Error): Promise<void> {
    if (this.reconnectingHubs.has(hubType)) {
      return;
    }

    this.reconnectingHubs.add(hubType);

    try {
      const closeMessage = error?.message ? `: ${error.message}` : '';
      const reconnectMessage = `[SignalR] ${hubType} hub closed${closeMessage}. Starting manual reconnect attempts.`;
      console.warn(reconnectMessage);
      this.messageLogger.logMessage(reconnectMessage);

      let attempt = 0;
      const startTime = Date.now();

      while (!this.isCleaningUp) {
        attempt += 1;

        const delayMs = attempt === 1 ? 0 : Math.min(1000 * 2 ** (attempt - 2), MANUAL_RECONNECT_MAX_DELAY_MS);
        if (delayMs > 0) {
          await this.delay(delayMs);
        }

        if (this.isCleaningUp) {
          return;
        }

        try {
          await hubService.ensureConnected();
          await this.restoreHubState(hubType);
          return;
        } catch {
          if (Date.now() - startTime > MANUAL_RECONNECT_GIVE_UP_MS) {
            const giveUpMessage = `[SignalR] ${hubType} hub: giving up manual reconnect after ${Math.round((Date.now() - startTime) / 1000).toString()}s.`;
            console.warn(giveUpMessage);
            this.messageLogger.logMessage(giveUpMessage);
            return;
          }
        }
      }
    } finally {
      this.reconnectingHubs.delete(hubType);
    }
  }

  private async restoreHubState(hubType: HubType): Promise<void> {
    const hubService = this.getHubService(hubType);
    const groupsToRestore = Array.from(this.currentGroups.entries()).filter(
      ([, groupData]) => groupData.hub === hubType
    );

    for (const [groupKey, groupData] of groupsToRestore) {
      try {
        this.registerEventHandlers(hubService, groupData.eventHandlers);
        await hubService.hubConnection.invoke('JoinGroup', groupKey);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        const logMessage = `[SignalR] Failed to restore group ${groupKey} (hub: ${hubType}): ${errorMessage}`;

        console.warn(logMessage);
        this.messageLogger.logMessage(logMessage);
      }
    }
  }

  private registerEventHandlers(hubService: HubService, eventHandlers: SignalREventHandler[]): void {
    eventHandlers.forEach(({ eventName, handler }) => {
      hubService.hubConnection.off(eventName, handler);
      hubService.hubConnection.on(eventName, handler);
    });
  }

  private unregisterEventHandlers(hubService: HubService, eventHandlers: SignalREventHandler[]): void {
    eventHandlers.forEach(({ eventName, handler }) => {
      hubService.hubConnection.off(eventName, handler);
    });
  }

  private async delay(delayMs: number): Promise<void> {
    await new Promise((resolve) => setTimeout(resolve, delayMs));
  }
}
